import { useEffect, useState } from 'react';
import { chatSend, climateLogo } from '../../../assets/images/images';
import { formatMessage, parseBulletPoints } from '../../../utility/helpers';

const IncomingText = ({
    chat,
    message,
    isLoading,
    isFromJob = false,
    setLastMessageId,
    handleSendMessage,
    setEnableGenerateResponse,
    setLastQuestionStep,
}: any) => {
    const isMultiSelect = chat?.is_multi_select;
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);

    const handleOptionClick = (option: string, key?: string) => {
        if (!isMultiSelect) {
            setSelectedOptions([option]);
            if (key) {
                handleSingleClick({[key] : option}, option);
            } else {
                handleSingleClick([option], option);
            }
        } else {
            setSelectedOptions((prevSelected) => {
                if (prevSelected.includes(option)) {
                    return prevSelected.filter((selected) => selected !== option);
                }
                return [...prevSelected, option];
            });
        }
    };

    const handleSingleClick = (data: any, option: any) => {
        handleSendMessage(data, option);
    };

    const handleMultiClick = () => {
        handleSendMessage(selectedOptions, '');
    };

    useEffect(() => {
        if (chat?.select_json?.length) {
            setSelectedOptions(chat.select_json);
        }
    }, [chat?.select_json]);

    useEffect(() => {
        if (chat.id) {
            setLastMessageId(chat.id);
        }
        if (chat?.step) {
            setLastQuestionStep(chat?.step);
        }
        // Backend may show the finish summary (result.step === 'finish') without is_finished.
        // Treat either signal as ready-to-generate so the button matches what Hunter shows.
        const isFinished = Boolean(
            chat?.is_finished ||
            message?.step === 'finish' ||
            chat?.result?.step === 'finish' ||
            chat?.step === 'finish'
        );
        if (isFinished) {
            setEnableGenerateResponse(true);
        }
    }, [chat, message, setLastQuestionStep, setLastMessageId, setEnableGenerateResponse]);

    const renderOptions = () => {
        if (message?.title && !Array.isArray(message?.title) && typeof message?.title === 'object') {
            return Object.entries(message.title).map(([key, value], index: number) => {
                const isSelected = selectedOptions.includes(value as string);
                return (
                    <div
                        className={`chat-option ${isSelected ? 'selected' : ''}`}
                        key={index}
                        onClick={() => handleOptionClick(value as string, key)}
                    >
                        <p className="chat-option-des">{value as string}</p>
                    </div>
                );
            });
        } else if (message?.title && Array.isArray(message?.title)) {
            return message.title.map((option: string, index: number) => {
                const isSelected = selectedOptions.includes(option);
                return (
                    <div
                        className={`chat-option ${isSelected ? 'selected' : ''}`}
                        key={index}
                        onClick={() => handleOptionClick(option)}
                    >
                        <p className="chat-option-des">{option}</p>
                    </div>
                );
            });
        }
        return null;
    };

    const generateDetailString = (details: Record<string, string | string[]>) => {
        return Object.entries(details).map(([key, value]) => {
            const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
            const formattedValue = Array.isArray(value) ? value.join(', ') : value;
    
            return (
                <div key={key} className="final-received">
                    <span className="inline-medium-text">{formattedKey}:</span> {formattedValue}
                </div>
            );
        });
    };


    const renderMessageContent = () => {
        // Safety check: if message is undefined or null, return empty
        if (!message) {
            return <p><span className="inline-bold-text">Hunter: </span></p>;
        }
        
        // Override the welcome message to use production format
        if (message?.is_initial && message?.message?.includes("Create a job post your way")) {
            const productionMessage = "Welcome! Let's build your job description. What position are you creating this for? Feel free to share the job title and any key details you have in mind.";
            return <p><span className="inline-bold-text">Hunter: </span>{productionMessage}</p>;
        }
        
        if (message?.message && Array.isArray(message?.message)) {
            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    <ul className="message-list">
                        {message.message.map((msg: any, index: any) => (
                            <li key={index}>{msg}</li>
                        ))}
                    </ul>
                </p>
            );
        } else if (message?.step === 'finish') {
            return (
                <p>
                    <span className="inline-bold-text">Hunter:</span>
                    Finished! These are details based on your provided job info:-
                    <br/><br/>
                    {message?.summary && (
                        generateDetailString(message?.summary)
                    )}
                    <br/>
                    Let me know if you want to change or update anything?
                </p>
            );
        }
        const messageText = 
            message?.step === 'distance'
                ? `${formatMessage(message?.message)} Distances are calculated using your office location to ensure precision.`
                : formatMessage(message?.message);

        // Check if message contains bullet points and render them as a list
        const bulletData = messageText ? parseBulletPoints(messageText) : null;
        
        if (bulletData?.bulletItems.length) {
            return (
                <p>
                    <span className="inline-bold-text">Hunter: </span>
                    {bulletData.introText && (
                        <>
                            <span>{bulletData.introText}</span>
                            <br />
                        </>
                    )}
                    <ul className="message-list">
                        {bulletData.bulletItems.map((item: string, index: number) => (
                            <li key={index}>{item}</li>
                        ))}
                    </ul>
                    {bulletData.closingText && (
                        <span>{bulletData.closingText}</span>
                    )}
                </p>
            );
        }

        return <p><span className="inline-bold-text">Hunter: </span>{messageText}</p>;
    };

    return (
        <>
            <div className="incoming_msg">
                <div className={`incoming_msg_img ${isFromJob ? 'd-none' : ''}`}>
                    <img
                        src={climateLogo}
                        alt="sunil"
                    />{' '}
                </div>
                <div className="received_msg">
                    <div className="received_withd_msg">
                        {renderMessageContent()}
                    </div>
                </div>
            </div>

            {((Array.isArray(message?.title) && message.title.length > 0) ||
                (!Array.isArray(message?.title) && (typeof message?.title === 'object'))) && (
                <div className={`chat-single-option ${(chat?.select_json || isLoading) ? 'p-none' : ''} ${isFromJob ? 'no-image-option' : ''}`}>
                    {renderOptions()}
                    {(isMultiSelect && !chat?.select_json) && (
                        <img
                            src={chatSend}
                            alt="send"
                            className={`chat-send-btn ${selectedOptions?.length > 0 ? '' : 'p-none'}`}
                            onClick={handleMultiClick}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default IncomingText;
